import { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "utils/@reduxjs/toolkit";
import {
  NotesHistoryItem,
  StatCardItem,
  VehicleDetailData,
  VehicleTableRow,
} from "../types";
import { initialState } from "./initialState";

const ensureSelectedVehicleDetail = (state: typeof initialState) => {
  if (!state.selectedVehicleDetail) {
    return null;
  }

  return state.selectedVehicleDetail;
};

export const vehicleStatusesSlice = createSlice({
  name: "vehicleStatusesState",
  initialState,
  reducers: {
    getList: (state) => {
      state.loading = true;
    },
    getListSuccess: (state, action: PayloadAction<VehicleTableRow[]>) => {
      state.loading = false;
      state.vehicles = action.payload;
    },
    getListFailure: (state) => {
      state.loading = false;
    },
    getVehicleDetail: (state, action: PayloadAction<number>) => {
      state.loading = true;
      state.selectedVehicleId = action.payload;
    },
    getVehicleDetailSuccess: (
      state,
      action: PayloadAction<{
        vehicleId: number;
        detail: VehicleDetailData;
      }>
    ) => {
      state.loading = false;
      state.selectedVehicleId = action.payload.vehicleId;
      state.selectedVehicleDetail = action.payload.detail;
    },
    getVehicleDetailFailure: (state) => {
      state.loading = false;
    },
    setStatCards: (state, action: PayloadAction<StatCardItem[]>) => {
      state.statCards = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setActiveStatusTab: (state, action: PayloadAction<number>) => {
      state.activeStatusTab = action.payload;
    },
    setActiveDetailTab: (state, action: PayloadAction<number>) => {
      state.activeDetailTab = action.payload;
    },
    setSelectedVehicleDetail: (
      state,
      action: PayloadAction<VehicleDetailData | null>
    ) => {
      state.selectedVehicleDetail = action.payload;
      state.selectedVehicleId = null;
    },
    updateProductionSubStatus: (state, action: PayloadAction<string>) => {
      const detail = ensureSelectedVehicleDetail(state);
      if (!detail) {
        return;
      }

      detail.production.status.subStatusValue = action.payload;
    },
    updateProductionItemValue: (
      state,
      action: PayloadAction<{ id: string; value: string }>
    ) => {
      const detail = ensureSelectedVehicleDetail(state);
      if (!detail) {
        return;
      }

      const item = detail.production.items.find(
        (entry) => entry.id === action.payload.id
      );
      if (item) {
        item.value = action.payload.value;
      }
    },
    updateWeightFieldValue: (
      state,
      action: PayloadAction<{ id: string; value: string }>
    ) => {
      const detail = ensureSelectedVehicleDetail(state);
      if (!detail) {
        return;
      }

      const field = detail.weights.fields.find(
        (entry) => entry.id === action.payload.id
      );
      if (field) {
        field.value = action.payload.value;
      }
    },
    updateTestsMarkingSheet: (state, action: PayloadAction<string>) => {
      const detail = ensureSelectedVehicleDetail(state);
      if (!detail) {
        return;
      }

      detail.tests.markingSheet.value = action.payload;
    },
    updateTestsInspectionDate: (state, action: PayloadAction<string>) => {
      const detail = ensureSelectedVehicleDetail(state);
      if (!detail) {
        return;
      }

      detail.tests.inspectionDate = action.payload;
    },
    setNotesDraft: (state, action: PayloadAction<string>) => {
      const detail = ensureSelectedVehicleDetail(state);
      if (!detail) {
        return;
      }

      detail.notes.draft = action.payload;
    },
    addNote: (state, action: PayloadAction<NotesHistoryItem>) => {
      const detail = ensureSelectedVehicleDetail(state);
      if (!detail) {
        return;
      }

      detail.notes.history = [action.payload, ...detail.notes.history];
      detail.notes.draft = "";
    },
  },
});

export const { reducer, actions, name: sliceKey } = vehicleStatusesSlice;
